import dayjs from 'dayjs-ext';
import i18n from 'i18next';
import { NBXMarkets } from 'services';
import { getChartMinTickInterval, roundToPrevTick } from 'helpers/dates';

// https://github.com/tradingview/charting_library/wiki/JS-Api

const CandlesticksDatafeedAdaptor = (
  market,
  refreshRate,
  configuration = {
    exchanges: [
      {
        value: 'NBX',
        name: 'NBX',
        description: i18n.t('market_trade_n_fly')
      }
    ],
    symbols_types: [],
    supported_resolutions: ['1', '5', '15', '60', '360', 'D'],
    supports_marks: false,
    supports_timescale_marks: false,
    supports_time: true,
    futures_regex: undefined
  }
) => {
  return {
    onReady: callback => {
      setTimeout(() => callback(configuration));
    },

    resolveSymbol: (symbolName, onSymbolResolvedCallback, onResolveErrorCallback) => {
      try {
        let assets = [];
        if (symbolName.includes('/')) {
          assets = symbolName.split(' / ');
          assets[0] = assets[0].split(':')[1]; // eslint-disable-line
        } else {
          assets = symbolName.split('-');
        }

        const minTickDecimal = Number(market.minTick).toString();
        let minmov;
        let pricescale;

        if (minTickDecimal.includes('e')) {
          const exponent = minTickDecimal.split('e')[1];
          const precision = Math.abs(parseInt(exponent, 10));
          minmov = 1;
          pricescale = Math.pow(10, precision);
        } else if (minTickDecimal.includes('.')) {
          const decimals = minTickDecimal.split('.')[1];
          minmov = parseInt(decimals, 10);
          pricescale = Math.pow(10, decimals.length);
        } else {
          minmov = 1;
          pricescale = 1;
        }

        setTimeout(() => {
          onSymbolResolvedCallback({
            name: `${assets[0]} / ${assets[1]}`,
            ticker: `${assets[0]}-${assets[1]}`,
            description: '',
            type: 'bitcoin',
            session: '24x7',
            exchange: 'NBX',
            listed_exchange: 'NBX',
            timezone: 'Etc/UTC',
            format: 'price',
            minmov,
            pricescale,
            has_intraday: true,
            supported_resolutions: configuration.supported_resolutions,
            has_daily: true,
            has_empty_bars: true,
            volume_precision: market.minQtyLength,
            data_status: 'pulsed'
          });
        });
      } catch (error) {
        setTimeout(() => {
          onResolveErrorCallback(error);
        });
      }
    },

    getBars: (symbolInfo, resolution, periodParams, onHistoryCallback, onErrorCallback) => {
      const pair = symbolInfo.ticker;
      const frequency = translateResolution(resolution);
      const minTick = getChartMinTickInterval(frequency);
      const fromISO = dayjs(roundToPrevTick(periodParams.from * 1000, minTick)).toISOString();
      const toISO = periodParams.firstDataRequest
        ? dayjs(new Date()).toISOString()
        : dayjs(roundToPrevTick(periodParams.to * 1000, minTick)).toISOString();

      NBXMarkets.getAndRenderCandlesticksForCharts(pair, frequency, fromISO, toISO)
        .then(candleSticks => {
          const translatedSticks = translateCandlesticks(candleSticks);
          onHistoryCallback(translatedSticks, { noData: candleSticks.length === 0 });
        })
        .catch(onErrorCallback);
    },

    subscribeBars: (
      symbolInfo,
      resolution,
      onRealtimeCallback,
      subscriberUID,
      onResetCacheNeededCallback
    ) => {
      const pair = symbolInfo.ticker;
      const frequency = translateResolution(resolution);
      const intervalRef = setInterval(() => {
        getMostRecentCandlestick(
          pair,
          frequency,
          subscriberUID,
          onRealtimeCallback,
          onResetCacheNeededCallback
        );
      }, refreshRate * 2);
      subscriptions[subscriberUID] = intervalRef;
    },

    unsubscribeBars: subscriberUID => {
      clearSubscription(subscriberUID);
    },

    getServerTime: callback => {
      callback(dayjs(new Date()).unix());
    },

    // Only applies to initial request
    calculateHistoryDepth: (resolution /* , resolutionBack, intervalBack */) => {
      // console.log('calculateHistoryDepth', resolution, resolutionBack, intervalBack);
      // Seems like tradingview always gets just over 2x the interval back
      // I can't get the interval to be exact
      switch (resolution) {
        case '1':
          return { resolutionBack: 'D', intervalBack: 0.020833333 / 2 };
        case '5':
          return { resolutionBack: 'D', intervalBack: 0.104166667 / 2 };
        case '15':
          return { resolutionBack: 'D', intervalBack: 0.3125 / 2 };
        case '60':
          return { resolutionBack: 'D', intervalBack: 1.25 / 2 };
        case '360':
          return { resolutionBack: 'D', intervalBack: 7.5 / 2 };
        case '1D':
        case 'D':
          return { resolutionBack: 'D', intervalBack: 30 / 2 };
        default:
          return undefined;
      }
    }
  };
};

function translateCandlesticks(candleSticks) {
  // our schema = [date, volume, open, high, low, close]
  // tv req schema = {time, close, open, high, low, volume}
  const translated = candleSticks.map(stick => {
    return {
      time: stick[0] * 1000,
      close: stick[5],
      open: stick[2],
      high: stick[3],
      low: stick[4],
      volume: stick[1]
    };
  });
  return translated;
}

function translateResolution(resolution) {
  // Translating from our backend freq schema to tv resolution schema
  switch (resolution) {
    case '1':
      return 'Min';
    case '5':
      return '5Min';
    case '15':
      return '15Min';
    case '60':
      return 'H';
    case '360':
      return '6H';
    case '1D':
      return 'D';
    default:
      return resolution;
  }
}

const subscriptions = {};

function getMostRecentCandlestick(pair, frequency, subscriberUID, callback, resetCacheCallback) {
  if (!subscriptions[subscriberUID]) return;
  const minTick = getChartMinTickInterval(frequency);
  // go back 2 ticks, that way the last candlestick will have a correct "open" value
  const fromMS = roundToPrevTick(dayjs(new Date()).valueOf(), minTick) - minTick;
  const fromISO = dayjs(fromMS).toISOString();
  const toISO = dayjs(new Date()).toISOString();
  NBXMarkets.getAndRenderCandlesticksForCharts(pair, frequency, fromISO, toISO)
    .then(candleSticks => {
      if (!subscriptions[subscriberUID]) return;
      if (candleSticks.length === 0) callback(null);
      else {
        const lastStick = translateCandlesticks(candleSticks.slice(-1))[0];
        callback(lastStick);
      }
    })
    .catch(resetCacheCallback);
}

function clearSubscription(subscriberUID) {
  const sub = subscriptions[subscriberUID];
  if (sub) {
    clearInterval(sub);
    delete subscriptions[subscriberUID];
  }
}

export default CandlesticksDatafeedAdaptor;
